import React, { useEffect, useMemo, useRef, useState } from "react";
import MDSpinner from "react-md-spinner";
import GoogleMapReact from "google-map-react";
import axios from "axios";
import { FaCompass, FaTimes } from "react-icons/fa";

import MapViewportToolbar from "../../components/MapViewportToolbar";
import myConfig from "../../configs/config";
import {
  MAP_VIEWPORT_SCOPES,
  applyViewportToGoogleMap,
  readLocalMapViewport,
  readViewportFromGoogleMap,
  resolveMapViewport,
  writeLocalMapViewport,
} from "../../utils/mapViewport";
import { applyGradualMapZoom } from "../../utils/gradualMapZoom";
import {
  buildBearingData,
  formatDistance,
  getMapOptions,
} from "./rumoPistaUtils";
import { useResizablePanel } from "./useResizablePanel";
import ServiceOrderMapOverlay from "./ServiceOrderMapOverlay";
import { releaseModalBlockers } from "../../utils/nestedMuiModalProps";
import "../RumoPistaAB/RumoPistaAB.css";
import "./TrackHeadingMapModal.css";

function TrackHeadingMapModal({
  open,
  farmId,
  onClose,
  onConfirm,
  t,
  initialBearingData,
  confirmLabel,
}) {
  const [loading, setLoading] = useState(true);
  const [farmCenter, setFarmCenter] = useState(null);
  const [savedMapViewport, setSavedMapViewport] = useState(null);
  const [bearingData, setBearingData] = useState(null);
  const [instruction, setInstruction] = useState(
    t("Clique no mapa para definir o Ponto A (Origem)")
  );

  const { rect, ResizeHandles } = useResizablePanel(open);

  const markerARef = useRef(null);
  const markerBRef = useRef(null);
  const lineRef = useRef(null);
  const clickCountRef = useRef(0);
  const mapApiRef = useRef(null);

  useEffect(() => {
    if (!open || !farmId) return;

    setLoading(true);
    setBearingData(null);
    setInstruction(t("Clique no mapa para definir o Ponto A (Origem)"));
    clickCountRef.current = 0;
    markerARef.current = null;
    markerBRef.current = null;
    lineRef.current = null;

    axios
      .get(`${myConfig.API_URL}/farm/${farmId}/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("client-token")}`,
        },
      })
      .then((res) => {
        setFarmCenter(res.data.farm_center);
        setSavedMapViewport(res.data.map_viewport || null);
        setLoading(false);
      })
      .catch(() => {
        setFarmCenter(null);
        setLoading(false);
      });
  }, [open, farmId, t]);

  const initialMapViewport = useMemo(() => {
    if (!farmCenter) return null;
    return resolveMapViewport({
      apiViewport: savedMapViewport,
      localViewport: readLocalMapViewport(
        MAP_VIEWPORT_SCOPES.TRACK_HEADING_MODAL,
        farmId
      ),
      fallbackCenter: farmCenter,
      fallbackZoom: 18,
    });
  }, [farmCenter, savedMapViewport, farmId]);

  function handleSaveMapViewport() {
    const map = mapApiRef.current && mapApiRef.current.map;
    if (!map) return;
    const viewport = readViewportFromGoogleMap(map);
    if (!viewport) return;
    writeLocalMapViewport(
      MAP_VIEWPORT_SCOPES.TRACK_HEADING_MODAL,
      farmId,
      viewport
    );
    setSavedMapViewport(viewport);
  }

  useEffect(() => {
    if (!open || !mapApiRef.current || !mapApiRef.current.map) return;
    const timer = setTimeout(() => {
      window.google.maps.event.trigger(mapApiRef.current.map, "resize");
      if (initialBearingData) {
        placeMarkersFromData(mapApiRef.current, initialBearingData);
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [open, rect.width, rect.height, initialBearingData]);

  function clearBearingTool() {
    if (markerARef.current) {
      markerARef.current.setMap(null);
      markerARef.current = null;
    }
    if (markerBRef.current) {
      markerBRef.current.setMap(null);
      markerBRef.current = null;
    }
    if (lineRef.current) {
      lineRef.current.setMap(null);
      lineRef.current = null;
    }
    clickCountRef.current = 0;
    setBearingData(null);
    setInstruction(t("Clique no mapa para definir o Ponto A (Origem)"));
  }

  function updateFromMarkers(pointA, pointB) {
    setBearingData(buildBearingData(pointA, pointB));
  }

  function updateLine(pointA, pointB) {
    if (lineRef.current) {
      lineRef.current.setPath([pointA, pointB]);
    }
  }

  function placeMarkersFromData(google, data) {
    if (!data || !data.pointA || !data.pointB) return;

    clearBearingTool();

    markerARef.current = new google.maps.Marker({
      position: data.pointA,
      map: google.map,
      label: { text: "A", color: "white", fontWeight: "bold" },
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 15,
        fillColor: "#4CAF50",
        fillOpacity: 1,
        strokeColor: "#2E7D32",
        strokeWeight: 2,
      },
      draggable: true,
    });

    markerBRef.current = new google.maps.Marker({
      position: data.pointB,
      map: google.map,
      label: { text: "B", color: "white", fontWeight: "bold" },
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 15,
        fillColor: "#F44336",
        fillOpacity: 1,
        strokeColor: "#C62828",
        strokeWeight: 2,
      },
      draggable: true,
    });

    lineRef.current = new google.maps.Polyline({
      path: [data.pointA, data.pointB],
      geodesic: true,
      strokeColor: "#2196F3",
      strokeOpacity: 1.0,
      strokeWeight: 3,
      map: google.map,
    });

    const attachDragListeners = () => {
      google.maps.event.addListener(markerARef.current, "drag", function () {
        const posA = markerARef.current.getPosition();
        const posB = markerBRef.current.getPosition();
        const pointA = { lat: posA.lat(), lng: posA.lng() };
        const pointB = { lat: posB.lat(), lng: posB.lng() };
        updateLine(pointA, pointB);
        updateFromMarkers(pointA, pointB);
      });
      google.maps.event.addListener(markerBRef.current, "drag", function () {
        const posA = markerARef.current.getPosition();
        const posB = markerBRef.current.getPosition();
        const pointA = { lat: posA.lat(), lng: posA.lng() };
        const pointB = { lat: posB.lat(), lng: posB.lng() };
        updateLine(pointA, pointB);
        updateFromMarkers(pointA, pointB);
      });
    };

    attachDragListeners();
    clickCountRef.current = 2;
    setBearingData(buildBearingData(data.pointA, data.pointB));
    setInstruction("");
  }

  function setupBearingTool(google) {
    mapApiRef.current = google;
    applyGradualMapZoom(google.map);
    if (initialMapViewport) {
      applyViewportToGoogleMap(google.map, initialMapViewport);
    }
    if (initialBearingData) {
      placeMarkersFromData(google, initialBearingData);
    }
    google.maps.event.addListener(google.map, "click", function (event) {
      if (event.domEvent) {
        event.domEvent.stopPropagation();
        event.domEvent.preventDefault();
      }

      const clickedLatLng = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      };

      if (clickCountRef.current === 0) {
        clearBearingTool();

        markerARef.current = new google.maps.Marker({
          position: clickedLatLng,
          map: google.map,
          label: { text: "A", color: "white", fontWeight: "bold" },
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 15,
            fillColor: "#4CAF50",
            fillOpacity: 1,
            strokeColor: "#2E7D32",
            strokeWeight: 2,
          },
          draggable: true,
        });

        google.maps.event.addListener(markerARef.current, "drag", function () {
          if (markerBRef.current) {
            const posA = markerARef.current.getPosition();
            const posB = markerBRef.current.getPosition();
            const pointA = { lat: posA.lat(), lng: posA.lng() };
            const pointB = { lat: posB.lat(), lng: posB.lng() };
            updateLine(pointA, pointB);
            updateFromMarkers(pointA, pointB);
          }
        });

        clickCountRef.current = 1;
        setInstruction(t("Clique no mapa para definir o Ponto B (Destino)"));
      } else if (clickCountRef.current === 1) {
        markerBRef.current = new google.maps.Marker({
          position: clickedLatLng,
          map: google.map,
          label: { text: "B", color: "white", fontWeight: "bold" },
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 15,
            fillColor: "#F44336",
            fillOpacity: 1,
            strokeColor: "#C62828",
            strokeWeight: 2,
          },
          draggable: true,
        });

        const posA = markerARef.current.getPosition();
        const pointA = { lat: posA.lat(), lng: posA.lng() };
        const pointB = clickedLatLng;

        lineRef.current = new google.maps.Polyline({
          path: [pointA, pointB],
          geodesic: true,
          strokeColor: "#2196F3",
          strokeOpacity: 1.0,
          strokeWeight: 3,
          map: google.map,
        });

        updateFromMarkers(pointA, pointB);

        google.maps.event.addListener(markerBRef.current, "drag", function () {
          const posAInner = markerARef.current.getPosition();
          const posBInner = markerBRef.current.getPosition();
          const pointAInner = { lat: posAInner.lat(), lng: posAInner.lng() };
          const pointBInner = { lat: posBInner.lat(), lng: posBInner.lng() };
          updateLine(pointAInner, pointBInner);
          updateFromMarkers(pointAInner, pointBInner);
        });

        clickCountRef.current = 2;
        setInstruction("");
      } else {
        clearBearingTool();
        clickCountRef.current = 0;
      }
    });
  }

  function handleConfirm() {
    if (!bearingData) return;
    onConfirm(bearingData);
    handleClose();
  }

  function handleClose() {
    onClose();
    releaseModalBlockers();
  }

  return (
    <ServiceOrderMapOverlay
      open={open}
      onClose={handleClose}
      ariaLabelledBy="track-heading-map-title"
    >
      <div
        className="track-heading-map-modal"
        style={{
          left: rect.x,
          top: rect.y,
          width: rect.width,
          height: rect.height,
        }}
      >
        <ResizeHandles />

        <div className="track-heading-map-modal__header">
          <h2 id="track-heading-map-title">
            <FaCompass style={{ marginRight: 8 }} />
            {t("Rumo de Pista A-B")}
          </h2>
          <button type="button" className="track-heading-map-modal__close" onClick={handleClose}>
            <FaTimes />
          </button>
        </div>

        <p className="track-heading-map-modal__hint">
          {t(
            "Marque A e B no satélite. Arraste os pontos para ajustar. Puxe as bordas da janela para ampliar o mapa e ajustar o zoom."
          )}
        </p>

        <div className="track-heading-map-modal__map">
          {loading || !farmCenter ? (
            <div className="rumo-spinner">
              <MDSpinner />
            </div>
          ) : (
            <div className="rumo-map-wrapper track-heading-map-modal__map-inner">
              <GoogleMapReact
                key={`track-heading-map-${farmId}`}
                bootstrapURLKeys={{ key: myConfig.GOOGLE_API_KEY }}
                options={getMapOptions}
                defaultCenter={
                  initialMapViewport
                    ? {
                        lat: initialMapViewport.lat,
                        lng: initialMapViewport.lng,
                      }
                    : { lat: farmCenter.ltd, lng: farmCenter.lng }
                }
                defaultZoom={initialMapViewport ? initialMapViewport.zoom : 18}
                yesIWantToUseGoogleMapApiInternals
                onGoogleApiLoaded={setupBearingTool}
              />
              <MapViewportToolbar
                onSave={handleSaveMapViewport}
                disabled={loading || !farmCenter}
                label={t("Salvar posição do mapa")}
              />
              {instruction ? (
                <div className="bearing-instructions">{instruction}</div>
              ) : null}
              {bearingData ? (
                <div className="bearing-card track-heading-map-modal__bearing-card">
                  <div className="bearing-card-body">
                    <div className="bearing-results">
                      <div className="bearing-result-item">
                        <div className="result-data">
                          <strong>{t("Rumo A→B")}</strong>
                          <span className="result-value bearing-value">
                            {bearingData.bearing.toFixed(2)}°
                          </span>
                        </div>
                      </div>
                      <div className="bearing-result-item">
                        <div className="result-data">
                          <strong>{t("Distância")}</strong>
                          <span className="result-value">
                            {formatDistance(bearingData.distance)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="track-heading-map-modal__coords">
                      <div>
                        A: {bearingData.pointA.lat.toFixed(6)},{" "}
                        {bearingData.pointA.lng.toFixed(6)}
                      </div>
                      <div>
                        B: {bearingData.pointB.lat.toFixed(6)},{" "}
                        {bearingData.pointB.lng.toFixed(6)}
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>

        <div className="track-heading-map-modal__actions">
          <button type="button" className="btn btn-default" onClick={handleClose}>
            {t("Cancelar")}
          </button>
          {bearingData ? (
            <button
              type="button"
              className="btn btn-link"
              onClick={clearBearingTool}
            >
              {t("Limpar pontos")}
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn-success"
            onClick={handleConfirm}
            disabled={!bearingData}
          >
            {confirmLabel || t("Usar este rumo")}
          </button>
        </div>
      </div>
    </ServiceOrderMapOverlay>
  );
}

export default TrackHeadingMapModal;
